import React, { useEffect, useState } from 'react';
import BottomNavigation from '../components/BottomNavigation';
import Header from '../components/Header';
import tonImage from '/ton.svg';
import tudImage from '/tud.svg';
import arrowImage from '/arrow_cool_down.svg';
import exchangeRateImage from '/published_with_changes.svg';
import { beginCell, toNano, fromNano, TonClient } from '@ton/ton';
import { DEX, pTON } from "@ston-fi/sdk";
import { useTonConnectUI, useTonAddress } from '@tonconnect/ui-react';

// STON API endpoints
const STON_API_URL = 'https://rpc.ston.fi';
const TON_ADDRESS = 'EQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAM9c';
const TUD_ADDRESS = 'EQDV99aHGIr1l15rkvdpyc56c_8Elsbn9NVNbok-09vxVAbZ';
const PROXY_TON_ADDRESS = 'EQBnGWMCf3-FZZq1W4IWcWiGAc3PHuZ0_H-7sad2oY00o83S';

const SwapPage: React.FC = () => {
  const [amount, setAmount] = useState(0);
  const [buttonText, setButtonText] = useState("Connect Wallet");
  const [tonBalance, setTonBalance] = useState(0);
  const [tudBalance, setTudBalance] = useState(0);
  const [isTonToTud, setIsTonToTud] = useState(true);
  const [swapRate, setSwapRate] = useState(500);
  const [swapFee, setSwapFee] = useState(0);
  const [swapFeePercent, setSwapFeePercent] = useState(0);
  const [requestId, setRequestId] = useState(11);
  const [isConnecting, setIsConnecting] = useState(false);
  const PurchaseBody = beginCell().storeUint(0, 32).storeStringTail("BUY").endCell();
  const [tonConnectUI, _] = useTonConnectUI();
  const address = useTonAddress();

  const handleDisconnect = async () => {
    try {
      setIsConnecting(true);
      await tonConnectUI.disconnect();
      setButtonText("Connect Wallet");
      setTonBalance(0);
      setTudBalance(0);
      setAmount(0);
    } catch (error) {
      console.error('Error disconnecting:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      await tonConnectUI.openModal();
    } catch (error) {
      console.error('Error connecting wallet:', error);
      setButtonText("Connect Wallet");
    } finally {
      setIsConnecting(false);
    }
  };

  useEffect(() => {
    if (tonConnectUI?.connected) {
      setButtonText("Swap");
    } else {
      setButtonText("Connect Wallet");
    }
  }, [tonConnectUI?.connected]);

  useEffect(() => {
    const fetchBalances = async () => {
      if (address) {
        try {
          // Fetch TON balance
          const tonResponse = await fetch(`https://toncenter.com/api/v2/getAddressBalance?address=${address}&api_key=b229bbed710ab2259b205d77526969e8ca83910eb81c9ff4facbf1c855b7ec3a`);
          const tonData = await tonResponse.json();
          if (tonData.ok) {
            setTonBalance(Number(fromNano(tonData.result)));
          }

          // Fetch TUD balance
          const tudResponse = await fetch(`https://toncenter.com/api/v3/jetton/wallets?owner_address=${address}&jetton_address=${TUD_ADDRESS}&limit=10&offset=0&api_key=b229bbed710ab2259b205d77526969e8ca83910eb81c9ff4facbf1c855b7ec3a`);
          const tudData = await tudResponse.json();
          if (tudData.jetton_wallets && tudData.jetton_wallets.length > 0) {
            const balance = tudData.jetton_wallets[0].balance;
            setTudBalance(Number(fromNano(balance)));
          }
        } catch (error) {
          console.error('Error fetching balances:', error);
        }
      }
    };

    fetchBalances();
    const balanceInterval = setInterval(fetchBalances, 15000);

    return () => clearInterval(balanceInterval);
  }, [address]);

  const simulateSwap = async (offerAddress: string, askAddress: string, offerUnits: string) => {
    try {
      const currentId = requestId;
      setRequestId(prev => prev + 1);
      
      const response = await fetch(STON_API_URL, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'pragma': 'no-cache'
        },
        body: JSON.stringify({
          jsonrpc: "2.0",
          id: currentId,
          method: "dex.simulate_swap",
          params: {
            offer_address: offerAddress,
            offer_units: offerUnits,
            ask_address: askAddress,
            slippage_tolerance: "0.49"
          }
        })
      });

      const data = await response.json();
      if (data.result) {
        return data.result;
      }
      throw new Error('Failed to simulate swap');
    } catch (error) {
      console.error('Error simulating swap:', error);
      throw error;
    }
  };

  useEffect(() => {
    const fetchSwapRate = async () => {
      if (!isTonToTud) {
        try {
          const result = await simulateSwap(TUD_ADDRESS, TON_ADDRESS, toNano(amount > 0 ? amount : 1).toString());
          if (result.ask_units) {
            const tonAmount = Number(fromNano(result.ask_units));
            const rate = tonAmount / (amount > 0 ? amount : 1);
            setSwapRate(rate);
            setSwapFee(Number(fromNano(result.fee_units)));
            setSwapFeePercent(Number(result.fee_percent) * 100);
          }
        } catch (error) {
          console.error('Error fetching swap rate:', error);
        }
      } else {
        setSwapRate(500);
        setSwapFee(0);
        setSwapFeePercent(0);
      }
    };

    fetchSwapRate();
    const rateInterval = setInterval(fetchSwapRate, 60000);

    return () => clearInterval(rateInterval);
  }, [isTonToTud, amount]);

  const handleSwapDirection = () => {
    setIsTonToTud(!isTonToTud);
    setAmount(0);
  };

  const handleMax = () => {
    setAmount(isTonToTud ? tonBalance : tudBalance);
  }

  const inputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    if (value === '') {
      setAmount(0);
      return;
    }

    const numericValue = value.replace(/^0+/, '') || '0';
    const parsedValue = parseFloat(numericValue);

    if (!isNaN(parsedValue) && parsedValue >= 0) {
      const maxValue = isTonToTud ? tonBalance : tudBalance;
      setAmount(parsedValue > maxValue ? maxValue : parsedValue);
    }
  }

  const formatAmount = (value: number) => {
    return value === 0 ? '' : value.toString();
  }

  const swapHandler = async () => {
    if (isConnecting) return;

    if (buttonText === "Swap") {
      if (isTonToTud) {
        const myTransaction = {
          validUntil: Math.floor(Date.now() / 1000) + 3600,
          messages: [
            {
              address: "EQAJ55efdfppttJ4jG5QdvD9u3ISsk-4CB88ro4ROlHZki5l",
              amount: toNano(amount).toString(),
              payload: PurchaseBody.toBoc().toString("base64")
            }
          ]
        };
        await tonConnectUI.sendTransaction(myTransaction);
      } else {
        try {
          if (!address) {
            throw new Error('Wallet not connected');
          }

          const result = await simulateSwap(TUD_ADDRESS, TON_ADDRESS, toNano(amount > 0 ? amount : 1).toString());
          if (!result) {
            throw new Error('Failed to simulate swap');
          }

          const client = new TonClient({
            endpoint: "https://toncenter.com/api/v2/jsonRPC",
          });
          
          const router = client.open(
            DEX.v2_2.Router.CPI.create(result.router_address)
          );
          
          const proxyTon = pTON.v2_1.create(PROXY_TON_ADDRESS);
          
          const txParams = await router.getSwapJettonToTonTxParams({
            userWalletAddress: address,
            offerJettonAddress: TUD_ADDRESS,
            offerAmount: toNano(amount.toString()),
            minAskAmount: result.min_ask_units,
            proxyTon,
            queryId: Math.floor(Date.now() / 1000),
          });
  
          await tonConnectUI.sendTransaction({
            validUntil: Date.now() + 1000000,
            messages: [
              {
                address: txParams.to.toString(),
                amount: txParams.value.toString(),
                payload: txParams.body?.toBoc().toString("base64"),
              },
            ],
          });
        } catch (error) {
          console.error('Error executing TUD to TON swap:', error);
        }
      }
    } else {
      await handleConnect();
    }
  };

  return (
    <div className="flex flex-col h-screen !bg-[#0F1620] text-white">
      <div className="flex-1 overflow-y-auto p-4">
        <Header />
        <div className="flex flex-col items-center">
          <div className="w-full bg-[rgba(113,171,255,0.1)] text-white p-4 rounded-lg">
            <div className="flex flex-row justify-between">
              <span className="text-md text-[#999999]">
                Balance : {(isTonToTud ? tonBalance : tudBalance).toFixed(4)}
              </span>
              <div className="flex gap-2">
                <button 
                  className="text-md rounded-lg text-[#439BDE] disabled:opacity-50" 
                  onClick={handleMax}
                  disabled={isConnecting}
                >
                  MAX
                </button>
                {tonConnectUI?.connected && (
                  <>
                    <div className="w-[1px] h-4 bg-[#439BDE] opacity-50 self-center" />
                    <button 
                      onClick={handleDisconnect}
                      className="text-md rounded-lg text-[#439BDE] disabled:opacity-50"
                      disabled={isConnecting}
                    >
                      DISCONNECT
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="w-full flex flex-row justify-between mt-2">
              <div className="flex items-center text-xl">
                <img src={isTonToTud ? tonImage : tudImage} alt={isTonToTud ? "TON" : "TUD"} className="w-6 h-6 me-2" />
                <span className="">{isTonToTud ? "TON" : "TUD"}</span>
              </div>
              <input
                type="number"
                className="text-xl bg-transparent text-white rounded-lg text-right outline-none w-full"
                onChange={inputChangeHandler}
                placeholder="0"
                value={formatAmount(amount)}
                min="0"
                step="any"
              />
            </div>
          </div>

          <button onClick={handleSwapDirection} className="z-10">
            <img src={arrowImage} alt="Swap" className="bg-[#303D51] rounded-full p-2 h-10 w-10 -mt-4 -mb-4" />
          </button>

          <div className="w-full bg-[rgba(113,171,255,0.1)] text-white p-4 rounded-lg">
            <div className="flex flex-row justify-between">
              <span className="text-md text-[#999999]">
                Receive
              </span>
            </div>
            <div className="w-full flex flex-row justify-between mt-2">
              <div className="flex items-center text-xl">
                <img src={isTonToTud ? tudImage : tonImage} alt={isTonToTud ? "TUD" : "TON"} className="w-6 h-6 me-2" />
                <span className="">{isTonToTud ? "TUD" : "TON"}</span>
              </div>
              <div className="">
                <span className="text-xl">{(amount * swapRate).toFixed(6)}</span>
              </div>
            </div>
          </div>

          <button className="w-full bg-[#50A8EB] text-white py-2 px-4 rounded-lg mt-4" onClick={swapHandler}>
            {buttonText}
          </button>
        </div>

        <div className="bg-[rgba(113,171,255,0.1)] rounded-lg p-4 mb-4 mt-6">
          <h3 className="text-lg font-semibold mb-2 flex items-center"><img src={exchangeRateImage} alt="Exchange Rate" className="w-6 h-6 me-2" /> Swap Detail</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-400">
                Exchange Rate
              </span>
              <span>{isTonToTud ? "1 TON = 500 TUD" : `1 TUD = ${swapRate.toFixed(6)} TON`}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Fee</span>
              <span>{isTonToTud ? "0 TON" : `${swapFee.toFixed(6)} TON (${swapFeePercent.toFixed(3)}%)`}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Powered by</span>
              <svg width="80" height="24" viewBox="0 0 107 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.6865 20.7432C14.8217 20.657 15.0088 20.7124 15.0643 20.8549L17.1662 26.2541C17.2338 26.4277 17.0635 26.6005 16.8693 26.5552L9.1305 24.7496C8.92237 24.701 8.87302 24.4494 9.04956 24.3369L14.6865 20.7432Z" fill="url(#paint0_linear_1799_10832)"></path><path d="M16.044 19.5515C16.0343 19.5265 16.0294 19.5003 16.0294 19.4738V14.0323C16.0294 13.8783 15.8686 13.7677 15.7083 13.8113L9.07702 15.616C8.91675 15.6597 8.75599 15.549 8.75599 15.3951V11.8784C8.75599 11.8065 8.79244 11.7388 8.85452 11.6952L17.496 5.63076C17.6161 5.54648 17.7893 5.57866 17.8645 5.69924L23.0847 14.0679C23.1217 14.1271 23.1282 14.1985 23.1024 14.2626L18.7074 25.1916C18.6263 25.3933 18.3161 25.3917 18.2373 25.1893L16.044 19.5515Z" fill="url(#paint1_linear_1799_10832)"></path><path d="M6.54328 6.10242C6.58502 6.06478 6.63974 6.04178 6.69796 6.03741L14.6737 5.43836C14.9229 5.41965 15.0445 5.71193 14.8455 5.85131L8.04736 10.6139C7.98444 10.6579 7.9026 10.672 7.82664 10.6519L3.35 9.46351C3.16587 9.41463 3.10735 9.20104 3.24427 9.07757L6.54328 6.10242Z" fill="url(#paint2_linear_1799_10832)"></path><path d="M2.10389 10.6593C2.12296 10.5188 2.27386 10.4272 2.42157 10.4666L7.16596 11.7298C7.27296 11.7583 7.34662 11.8485 7.34662 11.9511V17.0658C7.34662 17.2198 7.5075 17.3304 7.66781 17.2867L14.2531 15.49C14.4134 15.4462 14.5743 15.5569 14.5743 15.7109V18.9596C14.5743 19.035 14.5342 19.1057 14.4669 19.1488L7.08068 23.8767C6.98435 23.9384 6.85443 23.9301 6.76824 23.8568L1.07509 19.0164C1.0158 18.966 0.986554 18.8923 0.996563 18.8186L2.10389 10.6593Z" fill="url(#paint3_linear_1799_10832)"></path><path d="M35.8606 23.64C34.7248 23.64 33.6375 23.4867 32.5986 23.18C31.5598 22.86 30.7357 22.4533 30.1262 21.96L31.0612 19.94C31.6429 20.38 32.3632 20.7467 33.2219 21.04C34.0807 21.3333 34.9603 21.48 35.8606 21.48C36.6224 21.48 37.2388 21.4 37.7097 21.24C38.1807 21.08 38.5269 20.8667 38.7486 20.6C38.9702 20.32 39.081 20.0067 39.081 19.66C39.081 19.2333 38.9217 18.8933 38.6031 18.64C38.2845 18.3733 37.869 18.1667 37.3565 18.02C36.8579 17.86 36.2969 17.7133 35.6736 17.58C35.0641 17.4467 34.4478 17.2933 33.8245 17.12C33.215 16.9333 32.6541 16.7 32.1416 16.42C31.6429 16.1267 31.2343 15.74 30.9157 15.26C30.5972 14.78 30.4379 14.1667 30.4379 13.42C30.4379 12.66 30.6456 11.9667 31.0612 11.34C31.4906 10.7 32.1346 10.1933 32.9934 9.82C33.866 9.43333 34.9672 9.24 36.2969 9.24C37.1695 9.24 38.0352 9.34667 38.894 9.56C39.7528 9.77333 40.5007 10.08 41.1379 10.48L40.286 12.5C39.635 12.1267 38.9632 11.8533 38.2707 11.68C37.5781 11.4933 36.9133 11.4 36.2761 11.4C35.5282 11.4 34.9187 11.4867 34.4478 11.66C33.9907 11.8333 33.6513 12.06 33.4297 12.34C33.2219 12.62 33.1181 12.94 33.1181 13.3C33.1181 13.7267 33.2704 14.0733 33.5752 14.34C33.8937 14.5933 34.3023 14.7933 34.801 14.94C35.3135 15.0867 35.8814 15.2333 36.5047 15.38C37.128 15.5133 37.7443 15.6667 38.3538 15.84C38.9771 16.0133 39.5381 16.24 40.0367 16.52C40.5492 16.8 40.9578 17.18 41.2625 17.66C41.5811 18.14 41.7404 18.7467 41.7404 19.48C41.7404 20.2267 41.5257 20.92 41.0963 21.56C40.6808 22.1867 40.0367 22.6933 39.1641 23.08C38.2915 23.4533 37.1903 23.64 35.8606 23.64Z" fill="currentColor"></path><path d="M47.166 23.44V11.64H42.3458V9.44H54.6872V11.64H49.867V23.44H47.166Z" fill="currentColor"></path><path d="M63.3261 23.64C62.1903 23.64 61.1445 23.46 60.1888 23.1C59.2331 22.74 58.402 22.24 57.6956 21.6C56.9892 20.9467 56.4421 20.1867 56.0542 19.32C55.6664 18.44 55.4725 17.48 55.4725 16.44C55.4725 15.4 55.6664 14.4467 56.0542 13.58C56.4421 12.7 56.9892 11.94 57.6956 11.3C58.402 10.6467 59.2331 10.14 60.1888 9.78C61.1445 9.42 62.1834 9.24 63.3053 9.24C64.4411 9.24 65.4799 9.42 66.4218 9.78C67.3775 10.14 68.2086 10.6467 68.915 11.3C69.6214 11.94 70.1685 12.7 70.5564 13.58C70.9442 14.4467 71.1381 15.4 71.1381 16.44C71.1381 17.48 70.9442 18.44 70.5564 19.32C70.1685 20.2 69.6214 20.96 68.915 21.6C68.2086 22.24 67.3775 22.74 66.4218 23.1C65.4799 23.46 64.448 23.64 63.3261 23.64ZM63.3053 21.36C64.0394 21.36 64.7181 21.24 65.3414 21C65.9647 20.76 66.5049 20.42 66.962 19.98C67.4191 19.5267 67.7723 19.0067 68.0216 18.42C68.2848 17.82 68.4164 17.16 68.4164 16.44C68.4164 15.72 68.2848 15.0667 68.0216 14.48C67.7723 13.88 67.4191 13.36 66.962 12.92C66.5049 12.4667 65.9647 12.12 65.3414 11.88C64.7181 11.64 64.0394 11.52 63.3053 11.52C62.5712 11.52 61.8925 11.64 61.2692 11.88C60.6597 12.12 60.1195 12.4667 59.6486 12.92C59.1915 13.36 58.8314 13.88 58.5682 14.48C58.3189 15.0667 58.1942 15.72 58.1942 16.44C58.1942 17.1467 58.3189 17.8 58.5682 18.4C58.8314 19 59.1915 19.5267 59.6486 19.98C60.1057 20.42 60.6459 20.76 61.2692 21C61.8925 21.24 62.5712 21.36 63.3053 21.36Z" fill="currentColor"></path><path d="M74.0011 23.44V9.44H76.2242L85.3452 20.22H84.244V9.44H86.9242V23.44H84.7011L75.5801 12.66H76.6813V23.44H74.0011Z" fill="currentColor"></path><path d="M91.403 23.58C90.9321 23.58 90.5304 23.4267 90.198 23.12C89.8656 22.8 89.6993 22.4 89.6993 21.92C89.6993 21.4133 89.8656 21.0133 90.198 20.72C90.5304 20.4133 90.9321 20.26 91.403 20.26C91.874 20.26 92.2757 20.4133 92.6081 20.72C92.9405 21.0133 93.1067 21.4133 93.1067 21.92C93.1067 22.4 92.9405 22.8 92.6081 23.12C92.2757 23.4267 91.874 23.58 91.403 23.58Z" fill="currentColor"></path><path d="M95.8962 23.44V12.16C95.8962 11.04 96.2355 10.1467 96.9142 9.48C97.6068 8.8 98.5902 8.46 99.8645 8.46C100.322 8.46 100.751 8.50667 101.153 8.6C101.568 8.69333 101.914 8.84 102.192 9.04L101.485 10.92C101.291 10.7733 101.07 10.6667 100.82 10.6C100.571 10.52 100.315 10.48 100.052 10.48C99.5113 10.48 99.1027 10.6267 98.8257 10.92C98.5487 11.2 98.4101 11.6267 98.4101 12.2V13.4L98.4933 14.52V23.44H95.8962ZM94.0678 14.84V12.84H101.464V14.84H94.0678ZM103.293 23.44V12.76H105.89V23.44H103.293ZM104.602 11C104.117 11 103.715 10.8533 103.397 10.56C103.092 10.2667 102.939 9.91333 102.939 9.5C102.939 9.07333 103.092 8.72 103.397 8.44C103.715 8.14667 104.117 8 104.602 8C105.086 8 105.481 8.14 105.786 8.42C106.104 8.68667 106.264 9.02667 106.264 9.44C106.264 9.88 106.111 10.2533 105.807 10.56C105.502 10.8533 105.1 11 104.602 11Z" fill="currentColor"></path><defs><linearGradient id="paint0_linear_1799_10832" x1="18.5089" y1="9.39844" x2="1.82279" y2="27.333" gradientUnits="userSpaceOnUse"><stop offset="0.015625" stop-color="#3294FF"></stop><stop offset="1" stop-color="#005BBE"></stop></linearGradient><linearGradient id="paint1_linear_1799_10832" x1="18.5089" y1="9.39844" x2="1.82279" y2="27.333" gradientUnits="userSpaceOnUse"><stop offset="0.015625" stop-color="#3294FF"></stop><stop offset="1" stop-color="#005BBE"></stop></linearGradient><linearGradient id="paint2_linear_1799_10832" x1="18.5089" y1="9.39844" x2="1.82279" y2="27.333" gradientUnits="userSpaceOnUse"><stop offset="0.015625" stop-color="#3294FF"></stop><stop offset="1" stop-color="#005BBE"></stop></linearGradient><linearGradient id="paint3_linear_1799_10832" x1="18.5089" y1="9.39844" x2="1.82279" y2="27.333" gradientUnits="userSpaceOnUse"><stop offset="0.015625" stop-color="#3294FF"></stop><stop offset="1" stop-color="#005BBE"></stop></linearGradient></defs></svg>
            </div>
          </div>
        </div>
      </div>

      <BottomNavigation active="swap" />
    </div>
  );
};

export default SwapPage;